import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { verifyCosumerConsentGuard } from 'src/app/core/guards/consumer-consent.guard';
import { LogoutCallbackGuard } from 'src/app/core/guards/logout-callback.guard';
import { LogoutGuard } from 'src/app/core/guards/logout.guard';
import { verifyContactPhoneNumberGuard } from 'src/app/core/guards/verify-contact-phone-number.guard';
import { UserGuard } from './shared/guards/user.guard';

const routes: Routes = [
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginPageModule),
    data: { title: 'Login' },
  },
  {
    path: 'oauth-callback',
    loadChildren: () => import('./pages/oauth-callback/oath-callback.module').then((m) => m.OauthCallbackModule),
  },
  // TODO Get rid of LogoutGuard once the_tools_cut_over_to_fusion_auth_sign_in_hosted_uis FF is removed, change it to
  // { path: 'logout', redirectTo: `${environment.fusionAuthConfig.url}/oauth2/logout`, pathMatch: 'full' },
  {
    path: 'logout',
    loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginPageModule),
    canActivate: [LogoutGuard],
  },
  {
    path: 'logout-callback',
    loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginPageModule),
    canActivate: [LogoutCallbackGuard],
  },
  {
    path: 'update',
    loadChildren: () => import('./pages/updating/updating.module').then((m) => m.UpdatingPageModule),
    canDeactivate: [() => false],
  },
  {
    path: 'hard-cut-over-to-sp-app',
    loadChildren: () =>
      import('./pages/hard-cutover-to-sp-app/hard-cutover-to-sp-app.module').then(
        (m) => m.HardCutOverToSpAppPageModule,
      ),
  },
  {
    path: 'tabs',
    loadChildren: () => import('./pages/tabs/tabs.module').then((m) => m.TabsPageModule),
    data: { title: 'Tabs' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./pages/onboarding/onboarding.module').then((m) => m.OnboardingModule),
    data: { title: 'Account set up' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'search',
    loadChildren: () => import('./pages/search/search.module').then((m) => m.SearchPageModule),
    data: { title: 'Worker Search' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  // TODO: Route will change to original `messages` before UAT/Prod release
  {
    path: 'conversation/:id',
    loadChildren: () => import('./pages/conversation/conversation.module').then((m) => m.ConversationPageModule),
    data: { title: 'View Conversation' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'availability',
    loadChildren: () => import('./pages/availability/availability.module').then((m) => m.AvailabilityPageModule),
    data: { title: 'Manage availability' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  { path: 'conversations', redirectTo: 'tabs/inbox' },
  { path: 'jobs', redirectTo: 'tabs/jobs' },
  {
    path: 'jobs/view/:id',
    loadChildren: () => import('./pages/regular-jobs/view/regular-job-view.module').then((m) => m.RegularJobViewModule),
    data: { title: 'View Job' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  { path: 'agreements', redirectTo: 'tabs/agreements' },
  {
    path: 'agreement',
    loadChildren: () => import('./pages/agreements/agreements.module').then((m) => m.AgreementsPageModule),
  },
  { path: 'timesheets', redirectTo: 'tabs/timesheets' },
  {
    path: 'timesheet',
    loadChildren: () => import('./pages/timesheets/timesheets.module').then((m) => m.TimesheetsModule),
    data: { title: 'Support hours' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'support-hour',
    loadChildren: () => import('./pages/support-hour/support-hour.routes').then((m) => m.SUPPORT_HOUR_ROUTES),
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'accounts',
    loadChildren: () => import('./pages/accounts/accounts.module').then((m) => m.AccountsPageModule),
    data: { title: 'Accounts' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'shortlist',
    loadChildren: () => import('./pages/shortlist/shortlist.module').then((m) => m.ShortlistPageModule),
    data: { title: 'Shortlist' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'flex',
    loadChildren: () => import('./pages/flex/flex.module').then((m) => m.FlexModule),
    data: { title: 'Flex' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'private-jobs',
    loadChildren: () => import('./pages/private-jobs/private-jobs.module').then((m) => m.PrivateJobsModule),
    data: { title: 'Private Jobs' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'register',
    loadChildren: () => import('./pages/register/register.module').then((m) => m.RegisterPageModule),
    data: { title: 'Register' },
  },
  {
    path: 'consumer-consent',
    loadChildren: () => import('./pages/consumer-consent/consumer-consent.module').then((m) => m.ConsumerConsentModule),
    data: { title: 'Mandatory Consumer Consent' },
    canActivate: [UserGuard],
  },
  {
    path: 'mandatory-phone-verification',
    loadChildren: () =>
      import('./pages/mandatory-phone-verification/mandatory-phone-verification.module').then(
        (m) => m.MandatoryPhoneVerificationModule,
      ),
    data: { title: 'Mandatory Phone Verification' },
    canActivate: [UserGuard],
  },
  {
    path: 'identity-verification',
    loadChildren: () =>
      import('./pages/identity-verification/identity-verification.module').then((m) => m.IdentityVerificationModule),
    data: { title: 'Identity Verification' },
    canActivate: [UserGuard],
  },
  {
    path: 'meet-and-greet',
    loadChildren: () => import('./pages/meet-and-greet/meet-and-greet.module').then((m) => m.MeetAndGreetModule),
    data: { title: 'Meet And Greet' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'developer-handbook',
    loadChildren: () =>
      import('./pages/developer-handbook/developer-handbook.module').then((m) => m.DeveloperHandbookModule),
  },
  {
    path: 'feedback/:id',
    loadChildren: () => import('./pages/feedback/feedback.module').then((m) => m.FeedbackModule),
    data: { title: 'Feedback' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'client-account',
    loadChildren: () =>
      import('./pages/consumer-account/consumer-account.module').then((m) => m.ConsumerAccountPageModule),
    data: { title: 'Client Account' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'plan-session',
    loadChildren: () => import('./pages/scheduling/scheduling.module').then((m) => m.SchedulingModule),
    data: { title: 'Plan Session' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'event-list',
    loadChildren: () => import('./pages/scheduling/scheduling.module').then((m) => m.SchedulingModule),
    data: { title: 'Session Event List' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'post-jobs',
    loadChildren: () => import('./pages/post-jobs/post-jobs.module').then((m) => m.PostJobsModule),
    data: { title: 'Post jobs' },
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  {
    path: 'maintenance-downtime',
    loadChildren: () =>
      import('./pages/maintenance-downtime/maintenance-downtime.module').then((m) => m.MaintenanceDowntimePageModule),
  },
  {
    path: 'educate-support-worker',
    loadChildren: () =>
      import('./pages/educate-support-worker/educate-support-worker.module').then((m) => m.EducateSupportWorkerModule),
    canActivate: [UserGuard, verifyCosumerConsentGuard, verifyContactPhoneNumberGuard],
  },
  { path: '', redirectTo: 'tabs/home', pathMatch: 'full' },
  { path: '**', redirectTo: 'tabs/home' },
];
@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
